import { Separator } from '@crowdfpl/ui-web/separator';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Check, TrendingUp, Users } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getGameweeks } from '@/api/gameweeks';
import { getCurrentSeason } from '@/api/seasons';
import { EmailVerificationBanner } from '@/components/auth/EmailVerificationBanner';
import { AmbassadorStrip } from '@/components/home/AmbassadorStrip';
import { CrowdStatsDivider } from '@/components/home/CrowdStatsDivider';
import { HeroSection } from '@/components/home/HeroSection';
import { PopularDuelsSection } from '@/components/home/PopularDuelsSection';
import { TopManagersSection } from '@/components/home/TopManagersSection';
import { LeaderboardCard } from '@/components/leaderboard/LeaderboardCard';
import SEO from '@/components/SEO';
import { Footer } from '@/components/shared/layout/footer';
import { GlobalHeader } from '@/components/shared/layout/global-header';
import { MobileBottomNavigation } from '@/components/shared/layout/mobile-bottom-navigation';
import { MobileHeader } from '@/components/shared/layout/mobile-header';
import { SEO_DEFAULT_DESCRIPTION, SEO_DEFAULT_TITLE_SEGMENT } from '@/constants/seo-defaults';
import { trackUmamiEvent } from '@/helpers/analytics';
import { useAuth } from '@/hooks/auth';
import { useOptimizedDuels } from '@/hooks/data/duels';
import { useVoteOnDuel } from '@/hooks/data/duelVotes';
import { useTopClimbers } from '@/hooks/data/gameweekLeaderboard';
import { useGameweekStatuses } from '@/hooks/data/gameweekStatuses';
import { useOptimizedLeaderboard } from '@/hooks/data/leaderboard';
// import { useOptimizedStatistics } from '@/hooks/data/statistics';
// import { useVoteManager } from '@/hooks/data/voting';
import { useIsMobile, useIsMobileNav } from '@/hooks/utils/use-mobile';
import { useStableDuelOrder } from '@/hooks/utils/use-stable-duel-order';
import { notify } from '@/services/notifications';
import { useAuthModal } from '@/stores/ui';
import { useVotesStore } from '@/stores/votes';

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAuthModal } = useAuthModal();
  const { isAuthenticated } = useAuth();
  // Gameweek statuses - same logic as Duels page
  const { selectedGameweek } = useGameweekStatuses();

  // Get season and gameweeks list to find current gameweek with active deadline
  const seasonQuery = useQuery({
    queryKey: ['season', 'current'],
    queryFn: getCurrentSeason,
  });

  const gameweeksQuery = useQuery({
    queryKey: ['gameweeks', 'list', { seasonId: seasonQuery.data?.id }],
    queryFn: async () => {
      const res = await getGameweeks({ season_id: seasonQuery.data!.id, limit: 100, offset: 0, order: 'number.asc' });
      return res.gameweeks;
    },
    enabled: !!seasonQuery.data?.id,
    staleTime: 60 * 1000,
  });

  // Find gameweek that is_current and has deadline_time <= now (deadline has passed)
  const currentGameweekWithActiveDeadline = useMemo(() => {
    if (!gameweeksQuery.data) return null;
    const now = Date.now();
    return gameweeksQuery.data.find(
      (gw) => gw.is_current && gw.deadline_time && now >= new Date(gw.deadline_time).getTime(),
    ) || null;
  }, [gameweeksQuery.data]);

  // Check if current gameweek is live (is_current && now >= deadline)
  const isCurrentGameweekLive = useMemo(() => {
    if (!currentGameweekWithActiveDeadline || !currentGameweekWithActiveDeadline.deadline_time) return false;
    const now = Date.now();
    return currentGameweekWithActiveDeadline.is_current && now >= new Date(currentGameweekWithActiveDeadline.deadline_time).getTime();
  }, [currentGameweekWithActiveDeadline]);

  // Determine which gameweeks to show
  // Show two blocks only if current gameweek exists and is not finished
  const showTwoBlocks = !!currentGameweekWithActiveDeadline && currentGameweekWithActiveDeadline.finished === false;

  // Find next gameweek after currentGameweekWithActiveDeadline (for second block)
  const nextGameweek = useMemo(() => {
    if (!showTwoBlocks || !currentGameweekWithActiveDeadline || !gameweeksQuery.data) {
      return selectedGameweek;
    }
    // Find the next gameweek after currentGameweekWithActiveDeadline
    const currentNumber = currentGameweekWithActiveDeadline.number;
    return gameweeksQuery.data.find((gw) => gw.number === currentNumber + 1) || selectedGameweek;
  }, [showTwoBlocks, currentGameweekWithActiveDeadline, gameweeksQuery.data, selectedGameweek]);

  // Check if next gameweek (for second block) is live
  const isNextGameweekLive = useMemo(() => {
    if (!nextGameweek || !nextGameweek.deadline_time) return false;
    const now = Date.now();
    return nextGameweek.is_current && now >= new Date(nextGameweek.deadline_time).getTime();
  }, [nextGameweek]);
  const currentDuelsGameweekId = currentGameweekWithActiveDeadline?.id;
  const nextDuelsGameweekId = showTwoBlocks ? (nextGameweek?.id || selectedGameweek?.id) : selectedGameweek?.id;

  // Load duels for both gameweeks
  const { duels: currentDuels, loading: currentDuelsLoading } = useOptimizedDuels(
    showTwoBlocks && currentDuelsGameweekId ? { gameweekId: currentDuelsGameweekId } : {},
  );
  const { duels: nextDuels, loading: nextDuelsLoading } = useOptimizedDuels({
    gameweekId: showTwoBlocks ? nextDuelsGameweekId : selectedGameweek?.id,
  });

  // Use appropriate duels based on whether we show two blocks
  const duels = showTwoBlocks ? nextDuels : (nextDuels.length > 0 ? nextDuels : currentDuels);
  const duelsLoading = showTwoBlocks ? nextDuelsLoading : (nextDuelsLoading || currentDuelsLoading);

  const voteMutation = useVoteOnDuel();
  const { isVotingByDuelId, setVoting } = useVotesStore();
  const { leaderboard } = useOptimizedLeaderboard();
  const { data: topClimbersData, loading: topClimbersLoading } = useTopClimbers(seasonQuery.data?.id);
  // const { statistics, loading: statsLoading } = useOptimizedStatistics();
  const isMobile = useIsMobile();
  const isMobileNav = useIsMobileNav();

  useEffect(() => {
    if (location.state?.passwordResetSuccess) {
      notify.success('Password successfully updated! Please sign in with your new password.');
      showAuthModal();
      // Clean up state to prevent modal from reappearing on navigation
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, showAuthModal, navigate]);

  // Freeze home top-duel order on vote (reset when GW / block source changes)
  const orderedCurrentDuels = useStableDuelOrder(
    showTwoBlocks ? currentDuels : [],
    showTwoBlocks ? `home-current:${currentDuelsGameweekId || ''}` : '',
  );
  const nextDuelsList = showTwoBlocks ? nextDuels : duels;
  const nextDuelsScopeGw = showTwoBlocks ? nextDuelsGameweekId : selectedGameweek?.id;
  const orderedNextDuels = useStableDuelOrder(
    nextDuelsList,
    `home-next:${nextDuelsScopeGw || ''}`,
  );

  const topCurrentDuels = useMemo(
    () => (showTwoBlocks ? orderedCurrentDuels.slice(0, 4) : []),
    [orderedCurrentDuels, showTwoBlocks],
  );

  const topNextDuels = useMemo(() => orderedNextDuels.slice(0, 4), [orderedNextDuels]);

  // Get top 5 leaderboard entries
  const topLeaderboard = leaderboard.slice(0, 5);

  // Determine gameweek number for hero section
  // Always use the next/selected gameweek for hero section as per requirement
  const heroGameweekNumber = useMemo(() => {
    if (showTwoBlocks && nextGameweek) {
      return nextGameweek.number;
    }
    return selectedGameweek?.number;
  }, [showTwoBlocks, nextGameweek, selectedGameweek]);

  // Determine if hero gameweek is live
  const heroIsLiveGameweek = useMemo(() => {
    if (showTwoBlocks && nextGameweek) {
      return isNextGameweekLive;
    }
    return false;
  }, [showTwoBlocks, nextGameweek, isNextGameweekLive]);

  // Get top duel for hero section - Always use Next GW if available
  const topDuel = useMemo(() => {
    if (topNextDuels.length > 0) {
      return topNextDuels[0];
    }
    // Fallback to topCurrentDuels only if no topNextDuels (e.g. at the very end of season)
    if (topCurrentDuels.length > 0) {
      return topCurrentDuels[0];
    }
    return null;
  }, [topCurrentDuels, topNextDuels]);

  // Determine loading state for hero duel
  const heroDuelsLoading = useMemo(() => {
    // If we have next duels loading, use that
    return nextDuelsLoading || duelsLoading;
  }, [nextDuelsLoading, duelsLoading]);

  // Handler for voting in hero section
  const handleHeroVote = async (duelId: string, side: string) => {
    let duelsList: typeof duels;
    if (topNextDuels.length > 0) {
      // topDuel comes from topNextDuels
      duelsList = showTwoBlocks ? nextDuels : duels;
    } else {
      // fallback to currentDuels
      duelsList = currentDuels;
    }
    await handleVote(duelId, side, duelsList);
  };

  const handleVote = async (duel_id: string, side: string, duelsList: typeof duels = duels) => {
    if (!isAuthenticated) {
      showAuthModal();
      return;
    }
    const duel = duelsList.find((d) => d.id === duel_id);
    if (!duel) return;
    const selected_player_id =
      side === 'player1'
        ? (duel as { playerAId?: string }).playerAId
        : (duel as { playerBId?: string }).playerBId;
    if (!selected_player_id) return;

    setVoting(duel_id, true);
    try {
      await voteMutation.submitVote({ duel_id, selected_player_id });
    } finally {
      setVoting(duel_id, false);
    }
  };

  const handleCTAClick = () => {
    trackUmamiEvent('home_cta_make_your_choice');
    navigate('/duels');
  };

  const homeStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'CrowdFPL - Fantasy Premier League Player Duels',
    'description': 'Vote on 1-on-1 Fantasy Premier League player duels, see crowd odds, climb the leaderboard.',
    'breadcrumb': {
      '@type': 'BreadcrumbList',
      'itemListElement': [{
        '@type': 'ListItem',
        'position': 1,
        'name': 'Home',
        'item': 'https://crowdfpl.com/',
      }],
    },
    'mainEntity': {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'How does CrowdFPL work?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Pick winners in 1-on-1 player duels, earn rating points Gameweek, and rise to the top to prove your FPL skills.',
          },
        },
      ],
    },
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SEO
        title={SEO_DEFAULT_TITLE_SEGMENT}
        description={SEO_DEFAULT_DESCRIPTION}
        keywords="FPL predictions, Fantasy Premier League duels, FPL player comparison, gameweek decisions, FPL crowd wisdom, FPL tips"
        structuredData={homeStructuredData as Record<string, unknown>}
      />
      {/* Mobile Header - Mobile only */}
      {isMobileNav && <MobileHeader onAuthModalOpen={showAuthModal} />}

      {/* Global Header - Desktop only */}
      {!isMobileNav && <GlobalHeader onAuthModalOpen={showAuthModal} />}

      {/* Email Verification Banner - above hero section */}
      <div className="container mx-auto px-4">
        <EmailVerificationBanner className={isMobile ? 'mt-2' : 'mt-4'} />
      </div>

      {/* Hero Section */}
      <HeroSection
        gameweekNumber={heroGameweekNumber}
        isLiveGameweek={heroIsLiveGameweek}
        topDuel={topDuel}
        duelsLoading={heroDuelsLoading}
        onVote={handleHeroVote}
        isAuthenticated={isAuthenticated}
        isVotingByDuelId={isVotingByDuelId as Record<string, boolean | undefined>}
        onCTAClick={handleCTAClick}
      />

      <div className={`container mx-auto flex-1 px-4 py-3 space-y-4 ${isMobileNav ? 'pb-20' : ''}`}>
        {/* How it works - Info Strip */}
        <div className="pt-4 pb-6 md:pb-10">
          {isMobile ? (
            // Mobile: Compact badges
            <div className="max-w-4xl mx-auto px-4 space-y-4">
              <div className="px-2">
                <Separator />
              </div>
              {/* Tagline */}
              <p className="text-base text-muted-foreground text-center">
                Trust the crowd, lead the crowd
              </p>

              {/* Badges */}
              <div className="flex flex-wrap items-center justify-center gap-2">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-100/50 dark:bg-green-900/20 rounded-full border border-green-500/10">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="text-sm font-medium text-foreground">Weekly FPL Duels</span>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100/50 dark:bg-blue-900/20 rounded-full border border-blue-500/10">
                  <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium text-foreground">Rating System</span>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100/50 dark:bg-amber-900/20 rounded-full border border-amber-500/10">
                  <Users className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  <span className="text-sm font-medium text-foreground">Leaderboard</span>
                </div>
              </div>
            </div>
          ) : (
            // Desktop: Horizontal layout
            <div className="grid grid-cols-3 gap-8 max-w-5xl mx-auto px-4">
              {/* Weekly FPL duels */}
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex-shrink-0">
                  <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-base mb-0.5">Weekly FPL Duels</h3>
                  <p className="text-sm text-muted-foreground">
                    Pick winners in 1-on-1 player duels
                  </p>
                </div>
              </div>

              {/* Rating System */}
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex-shrink-0">
                  <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-base mb-0.5">Rating System</h3>
                  <p className="text-sm text-muted-foreground">
                    Earn points with correct votes each Gameweek
                  </p>
                </div>
              </div>

              {/* Leaderboard */}
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex-shrink-0">
                  <Users className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-base mb-0.5">Leaderboard</h3>
                  <p className="text-sm text-muted-foreground">
                    Rise to the top and prove your FPL skills
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <CrowdStatsDivider />

        <AmbassadorStrip />
        <Separator className="my-6" />

        {/* Top-3 Managers GW Section */}
        {topClimbersData && topClimbersData.top_climbers.length > 0 && (
          <>
            <div className="pb-4 pt-4">
              <TopManagersSection
                gameweekNumber={topClimbersData.gameweek_number}
                topClimbers={topClimbersData.top_climbers}
                loading={topClimbersLoading}
              />
            </div>
            <Separator className="my-6" />
          </>
        )}

        {/* Popular duels for next gameweek */}
        <PopularDuelsSection
          title="Top Duels (Next GW)"
          duels={topNextDuels.filter(d => d.id !== topDuel?.id).slice(0, 3)}
          loading={duelsLoading}
          trackEventName="home_popular_duels_view_all"
          gameweekId={showTwoBlocks ? nextDuelsGameweekId : selectedGameweek?.id}
          onVote={(id, side) => handleVote(id, side, showTwoBlocks ? nextDuels : duels)}
          isAuthenticated={isAuthenticated}
          isVotingByDuelId={isVotingByDuelId as Record<string, boolean | undefined>}
          isLiveGameweek={isNextGameweekLive}
          showSolioXpts={true}
        />

        {/* Popular duels for current gameweek (is_current && now >= deadline) */}
        {showTwoBlocks && (
          <PopularDuelsSection
            title="Top Duels (Current GW)"
            duels={topCurrentDuels.filter(d => d.id !== topDuel?.id).slice(0, 3)}
            loading={currentDuelsLoading}
            trackEventName="home_popular_duels_view_all"
            showPulsingIndicator={true}
            gameweekId={currentDuelsGameweekId}
            onVote={(id, side) => handleVote(id, side, currentDuels)}
            isAuthenticated={isAuthenticated}
            isVotingByDuelId={isVotingByDuelId as Record<string, boolean | undefined>}
            isLiveGameweek={isCurrentGameweekLive}
            showSolioXpts={true}
          />
        )}

        {/* Divider */}
        <Separator className="my-6" />

        {/* Top Leaderboard Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="mb-3 text-center text-lg font-bold text-foreground md:mb-4 md:text-left md:text-xl">All-Time Top Managers</h2>
            <Link
              to="/leaderboard"
              onClick={() => trackUmamiEvent('home_top_managers_view_all')}
              className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground ${isMobile ? 'h-8 px-3 text-xs' : 'h-10 px-4 text-sm'}`}
            >
              View all
              <ArrowRight className="h-3 w-3 md:h-4 md:w-4" />
            </Link>
          </div>
          <LeaderboardCard
            entries={topLeaderboard}
            title=""
            onUserClick={(username) => trackUmamiEvent('home_top_manager_click', { username })}
          />
        </div>
      </div>

      <Footer />

      {/* Mobile Bottom Navigation */}
      {isMobileNav && <MobileBottomNavigation />}

    </div>
  );
};

export default Home;
